import { useMemo, useState, useEffect } from 'react';
import { useDialogA11y } from '../../lib/use-dialog-a11y.js';
import { useQueries } from '@tanstack/react-query';
import {
  Check,
  Loader2,
  Package,
  RotateCcw,
  ShoppingBasket,
  Warehouse,
  X,
} from 'lucide-react';
import { api, type SalesOrder, type SalesOrderSummary } from '../../lib/api.js';
import { Feedback } from '../ui/feedback.js';

interface ShoppingListSheetProps {
  /** Today's open WOs assigned to the tech — drives which detail fetches we trigger. */
  ordersForToday: ReadonlyArray<SalesOrderSummary>;
  /** Tech id used in the localStorage key so two techs sharing a tablet
   * don't see each other's check state. */
  techId: string;
  /** Today's local-time ISO date (YYYY-MM-DD) — scopes the persisted state so a
   * tech who comes in tomorrow gets a fresh checklist. */
  todayIso: string;
  onClose: () => void;
}

/**
 * Aggregated truck-stocking checklist. Pulls the SO detail for each of
 * today's WOs in parallel (TanStack Query caches them so re-opening is
 * instant), then groups line items by (partNo, warehouse) and sums the
 * required quantities across orders.
 *
 * Picked state persists in localStorage keyed on `tech + day` — checkboxes
 * survive a tab close + reopen during the same workday but reset for
 * tomorrow's prep. The "Reset" button clears just today's check state.
 *
 * Read-only with respect to Spire: this is a morning-prep tool, not a
 * picking-confirmation workflow. Checking a row writes nothing to the SO.
 */
export function ShoppingListSheet({
  ordersForToday,
  techId,
  todayIso,
  onClose,
}: ShoppingListSheetProps) {
  const orderIds = ordersForToday.map((o) => o.spireId);

  // Parallel detail fetches — each is cached under the same key the
  // job-preview modal uses, so opening either screen first warms the
  // cache for the other.
  const detailQueries = useQueries({
    queries: orderIds.map((id) => ({
      queryKey: ['sales-orders', 'detail', id] as const,
      queryFn: ({ signal }: { signal?: AbortSignal }) => api.salesOrders.getById(id, signal),
      staleTime: 30_000,
    })),
  });

  const anyPending = detailQueries.some((q) => q.isPending);
  const errors = detailQueries.filter((q) => q.isError);
  const details = detailQueries
    .map((q) => q.data)
    .filter((d): d is SalesOrder => d !== undefined);

  const rows = useMemo(() => aggregateLineItems(details), [details]);

  const storageKey = `tinsmith.shopping-list.${techId}.${todayIso}`;
  const [checked, setChecked] = useState<Set<string>>(() => loadCheckedSet(storageKey));

  // Persist any change so a refresh / tab close mid-pick doesn't lose
  // the tech's progress. Keyed on the stored set + key so we only write
  // on actual diffs.
  useEffect(() => {
    saveCheckedSet(storageKey, checked);
  }, [storageKey, checked]);

  const toggle = (key: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const resetChecks = () => setChecked(new Set());
  const checkAll = () => setChecked(new Set(rows.map((r) => r.key)));

  const totalRows = rows.length;
  const pickedRows = rows.filter((r) => checked.has(r.key)).length;
  const allPicked = totalRows > 0 && pickedRows === totalRows;

  const dialogRef = useDialogA11y<HTMLDivElement>(onClose);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Shopping list — today's parts"
      className="fixed inset-0 z-40 bg-slate-950/40 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md max-h-[90vh] flex flex-col rounded-xl shadow-xl overflow-hidden"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
      >
        <header
          className="flex items-start justify-between gap-2 px-4 py-3 border-b"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ShoppingBasket className="h-4 w-4 text-ink-accent" aria-hidden />
              <div className="text-sm font-semibold text-ink">Shopping list — today</div>
            </div>
            <div className="text-[11px] text-ink-muted mt-0.5">
              {anyPending
                ? 'Loading parts…'
                : totalRows === 0
                  ? 'No parts on today\'s work orders.'
                  : `${pickedRows}/${totalRows} picked · across ${ordersForToday.length} ${ordersForToday.length === 1 ? 'order' : 'orders'}`}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close shopping list"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink shrink-0"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        {totalRows > 0 && (
          <div
            className="flex items-center gap-2 px-3 py-2 border-b text-[11px]"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          >
            <div className="flex-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
              <div
                className="h-full transition-all"
                style={{
                  width: `${(pickedRows / totalRows) * 100}%`,
                  backgroundColor: allPicked
                    ? 'var(--feedback-success-fg)'
                    : 'var(--color-text-info)',
                }}
              />
            </div>
            <button
              type="button"
              onClick={checkAll}
              disabled={allPicked}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-ink-muted hover:bg-surface-muted hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Check className="h-3.5 w-3.5" aria-hidden />
              All
            </button>
            <button
              type="button"
              onClick={resetChecks}
              disabled={pickedRows === 0}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-ink-muted hover:bg-surface-muted hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Reset
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-3 py-2">
          {anyPending && (
            <div className="flex items-center justify-center py-6 text-ink-muted text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden />
              Aggregating parts from today's orders…
            </div>
          )}
          {errors.length > 0 && (
            <Feedback variant="warning" size="sm">
              Couldn't load {errors.length} of {detailQueries.length} order
              {detailQueries.length === 1 ? '' : 's'} — list may be incomplete.
              Pull down to refresh once you have signal.
            </Feedback>
          )}
          {!anyPending && totalRows === 0 && (
            <div
              className="rounded-md border bg-surface px-4 py-6 text-center text-sm text-ink-muted"
              style={{ borderColor: 'var(--color-border-tertiary)' }}
            >
              <Package className="h-6 w-6 mx-auto text-ink-subtle" aria-hidden />
              <div className="mt-2">No parts on today's work orders to pre-pick.</div>
            </div>
          )}
          {totalRows > 0 && (
            <ul className="space-y-1.5">
              {rows.map((row) => {
                const isPicked = checked.has(row.key);
                return (
                  <li key={row.key}>
                    <button
                      type="button"
                      onClick={() => toggle(row.key)}
                      aria-pressed={isPicked}
                      className={`w-full grid grid-cols-[24px_1fr_auto] items-start gap-2 rounded-md border px-2.5 py-2 text-left transition-colors ${
                        isPicked
                          ? 'bg-emerald-50/50 border-emerald-200'
                          : 'bg-surface hover:bg-surface-muted'
                      }`}
                      style={
                        isPicked
                          ? undefined
                          : { borderColor: 'var(--color-border-tertiary)' }
                      }
                    >
                      <span
                        aria-hidden
                        className={`inline-flex h-5 w-5 mt-0.5 items-center justify-center rounded border ${
                          isPicked
                            ? 'bg-emerald-600 border-emerald-600 text-white'
                            : 'bg-surface border-line-soft'
                        }`}
                      >
                        {isPicked && <Check className="h-3 w-3" />}
                      </span>
                      <div className="min-w-0">
                        <div
                          className={`text-sm font-medium truncate ${
                            isPicked ? 'text-ink-muted line-through' : 'text-ink'
                          }`}
                        >
                          {row.description || row.partNo}
                        </div>
                        <div className="text-[10.5px] font-mono text-ink-muted">
                          {row.partNo}
                          {row.warehouse && (
                            <>
                              <span aria-hidden> · </span>
                              <span title={`Warehouse ${row.warehouse}`} className="inline-flex items-center gap-0.5">
                                <Warehouse className="h-3 w-3" aria-hidden />
                                {row.warehouse}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="text-[10px] text-ink-subtle mt-0.5 truncate">
                          for {row.orderNos.join(', ')}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm tabular-nums font-medium">
                          {row.totalQty}
                        </div>
                        <div className="text-[10px] text-ink-subtle">{row.uom}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer
          className="px-4 py-3 border-t text-[11px] text-ink-subtle"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
        >
          Picks save locally on this device. Reset clears just today.
          Tomorrow's list starts fresh.
        </footer>
      </div>
    </div>
  );
}

/**
 * One aggregated row in the shopping list — sums quantity across every
 * line on every WO for the same `(partNo, warehouse)` tuple. The key is
 * stable across re-fetches so the local "picked" set survives a re-pull.
 */
interface ShoppingRow {
  /** `partNo:whse` — stable across re-fetches. */
  key: string;
  partNo: string;
  description: string;
  warehouse: string;
  /** Sum of orderQty across all WOs, rendered as a tidy integer-ish string. */
  totalQty: string;
  /** Best-effort unit label; "EA" when sellMeasure is missing on every line. */
  uom: string;
  /** Order numbers contributing to this row — surfaced as "for SO123, SO124". */
  orderNos: string[];
}

function aggregateLineItems(orders: ReadonlyArray<SalesOrder>): ShoppingRow[] {
  const byKey = new Map<
    string,
    {
      partNo: string;
      description: string;
      warehouse: string;
      qty: number;
      uom: string;
      orderNos: Set<string>;
    }
  >();

  for (const order of orders) {
    for (const line of order.items) {
      // Skip comment-only lines (no part number). Spire stores those for
      // human-readable notes — useful in detail view, irrelevant for
      // truck stocking.
      if (!line.partNo || !line.partNo.trim()) continue;
      const whse = line.whseCode || '';
      const key = `${line.partNo.trim()}:${whse}`;
      const qty = Number(line.orderQty);
      if (!Number.isFinite(qty)) continue;
      const existing = byKey.get(key);
      if (existing) {
        existing.qty += qty;
        existing.orderNos.add(order.orderNo);
      } else {
        byKey.set(key, {
          partNo: line.partNo.trim(),
          description: line.description ?? '',
          warehouse: whse,
          qty,
          uom: line.sellMeasure ?? 'EA',
          orderNos: new Set([order.orderNo]),
        });
      }
    }
  }

  return Array.from(byKey.values())
    .map(({ partNo, description, warehouse, qty, uom, orderNos }) => ({
      key: `${partNo}:${warehouse}`,
      partNo,
      description,
      warehouse,
      totalQty: formatQty(qty),
      uom,
      orderNos: Array.from(orderNos).sort(),
    }))
    .sort((a, b) => a.partNo.localeCompare(b.partNo));
}

/** Render quantity with no trailing zeros (e.g. `3` not `3.000`). */
function formatQty(qty: number): string {
  if (Number.isInteger(qty)) return String(qty);
  return qty.toFixed(2).replace(/\.?0+$/, '');
}

function loadCheckedSet(storageKey: string): Set<string> {
  if (typeof window === 'undefined' || !window.localStorage) return new Set();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function saveCheckedSet(storageKey: string, set: Set<string>): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(Array.from(set)));
  } catch {
    // Quota / private-mode — silent.
  }
}
